import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSearch } from '../context/searchcontext'
import { performLocationSearch } from '../utils/searchService'
import { buildSlugPath } from '../utils/slugutils'
import Toast from './Toast'

const GENDER_OPTIONS = ['Any Gender', 'Boys', 'Girls', 'Co-ed']
const PRICE_OPTIONS = ['Any Price', 'Under ₹5,000', '₹5,000–₹8,000', '₹8,000–₹12,000', 'Above ₹12,000']
const SHARING_OPTIONS = ['Any Type', 'Single', 'Double', 'Triple']

const SearchBar = ({ compact = false, onAction = null }) => {
  const navigate = useNavigate()
  const { 
    location, setLocation, 
    gender, setGender, 
    price, setPrice, 
    sharing, setSharing, 
    setSearchResults, 
    setNearbyPincodes, 
    setSearchCoordinates,
    setIsSearching
  } = useSearch()

  // Desktop state
  const [activePanel, setActivePanel] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  
  // Mobile state - same as desktop
  const [mobileSearchQuery, setMobileSearchQuery] = useState('')
  const [mobileSuggestions, setMobileSuggestions] = useState([])
  const [showMobileDropdown, setShowMobileDropdown] = useState(false)
  const [mobileActiveIndex, setMobileActiveIndex] = useState(-1)
  
  // Mobile modal state
  const [mobileModal, setMobileModal] = useState(null)
  const [tempValue, setTempValue] = useState('')
  
  const [isLoading, setIsLoading] = useState(false)
  const [toast, setToast] = useState(null)
  
  const barRef = useRef(null)
  const searchTimeoutRef = useRef(null)
  const mobileSearchTimeoutRef = useRef(null)

  const showToast = (message, type = 'error') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // ── New Autocomplete using custom API ─────────────────────────────────────────
  const fetchSuggestions = async (query, isMobile = false) => {
    if (!query || query.length < 2) {
      if (isMobile) {
        setMobileSuggestions([])
        setShowMobileDropdown(false)
      } else {
        setSuggestions([])
        setShowDropdown(false)
      }
      return
    }

    try {
      const url = `https://ciplpgapi.azurewebsites.net/pgmaster/listing-location-search/${encodeURIComponent(query)}`
      console.log('🔍 Fetching suggestions from:', url)
      
      const response = await fetch(url, {
        headers: { 
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      console.log('📦 Suggestions received:', data)
      
      // Transform API response to match the expected format
      // Expected format from your API: [{"SearchValue":"","SearchType":""}]
      const transformedSuggestions = (data || []).map(item => ({
        display_name: item.SearchValue,
        place_id: item.SearchValue, // Use SearchValue as unique key
        type: item.SearchType
      }))
      
      if (isMobile) {
        setMobileSuggestions(transformedSuggestions)
        setShowMobileDropdown(transformedSuggestions.length > 0)
        setMobileActiveIndex(-1)
      } else {
        setSuggestions(transformedSuggestions)
        setShowDropdown(transformedSuggestions.length > 0)
        setActiveIndex(-1)
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error)
      if (isMobile) {
        setMobileSuggestions([])
        setShowMobileDropdown(false)
      } else {
        setSuggestions([])
        setShowDropdown(false)
      }
    }
  }

  // Desktop input change
  const handleInputChange = (e) => {
    const value = e.target.value
    setSearchQuery(value)
    setLocation(value)
    
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(() => {
      fetchSuggestions(value, false)
    }, 300)
  }

  // Mobile input change - SAME as desktop
  const handleMobileInputChange = (e) => {
    const value = e.target.value
    setMobileSearchQuery(value)
    setLocation(value)
    
    if (mobileSearchTimeoutRef.current) clearTimeout(mobileSearchTimeoutRef.current)
    mobileSearchTimeoutRef.current = setTimeout(() => {
      fetchSuggestions(value, true)
    }, 300)
  }

  // Desktop search
  const handleSearch = async (queryToSearch) => {
    const searchTerm = queryToSearch || searchQuery || location
    if (!searchTerm) {
      showToast('Please enter a location to search', 'warning')
      return
    }
    
    setIsLoading(true)
    setIsSearching(true)
    setShowDropdown(false)
    setMobileModal(null)
    
    // Close mobile menu if this is mobile search
    if (onAction) onAction()
    
    const result = await performLocationSearch(searchTerm, gender, price, sharing)
    
    if (result.success) {
      const isCitySearch = searchTerm.toLowerCase().includes('delhi')
      if (result.results.length === 0 && !isCitySearch) {
        showToast(`No PGs found in ${searchTerm}. Try a different location.`, 'info')
      }
      setSearchResults(result.results)
      setNearbyPincodes(result.nearbyPincodes)
      setSearchCoordinates(result.coordinates)
      setLocation(searchTerm)
      setSearchQuery(searchTerm)
      setMobileSearchQuery(searchTerm)
      
      // Navigate to SEO-friendly slug URL
      const slugPath = buildSlugPath(
        searchTerm,
        gender !== 'Any Gender' ? gender : '',
        price  !== 'Any Price'  ? price  : '',
        sharing !== 'Any Type'  ? sharing : ''
      )
      navigate(slugPath)
    } else {
      showToast(result.error || 'Location not found. Please try a different area.', 'error')
    }
    
    setIsLoading(false)
    setIsSearching(false)
  }

  // Mobile search - SAME as desktop
  const handleMobileSearch = async (queryToSearch) => {
    const searchTerm = queryToSearch || mobileSearchQuery || location
    if (!searchTerm) {
      showToast('Please enter a location to search', 'warning')
      return
    }
    
    setIsLoading(true)
    setIsSearching(true)
    setShowMobileDropdown(false)
    setMobileModal(null)

    // Close mobile menu
    if (onAction) onAction()
    
    const result = await performLocationSearch(searchTerm, gender, price, sharing)
    
    if (result.success) {
      const isCitySearch = searchTerm.toLowerCase().includes('delhi')
      if (result.results.length === 0 && !isCitySearch) {
        showToast(`No PGs found in ${searchTerm}. Try a different location.`, 'info')
      }
      setSearchResults(result.results)
      setNearbyPincodes(result.nearbyPincodes)
      setSearchCoordinates(result.coordinates)
      setLocation(searchTerm)
      setSearchQuery(searchTerm)
      setMobileSearchQuery(searchTerm)
      
      // Navigate to SEO-friendly slug URL
      const slugPath = buildSlugPath(
        searchTerm,
        gender !== 'Any Gender' ? gender : '',
        price  !== 'Any Price'  ? price  : '',
        sharing !== 'Any Type'  ? sharing : ''
      )
      navigate(slugPath)
    } else {
      showToast(result.error || 'Location not found. Please try a different area.', 'error')
    }
    
    setIsLoading(false)
    setIsSearching(false)
  }

  const handleGeolocation = async () => {
    if (!navigator.geolocation) {
      showToast('Geolocation is not supported by your browser', 'error')
      return
    }
    
    setIsLoading(true)
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords
        const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
        
        try {
          const response = await fetch(url, { headers: { 'User-Agent': 'PGApp/1.0' } })
          const data = await response.json()
          const areaName = data.display_name?.split(',')[0] || data.address?.suburb || data.address?.neighbourhood
          setMobileSearchQuery(areaName)
          setSearchQuery(areaName)
          
          // Close mobile menu
          if (onAction) onAction()
          
          await handleMobileSearch(areaName)
        } catch (error) {
          console.error('Geolocation error:', error)
          showToast('Could not get your location. Please search manually.', 'error')
        }
        setIsLoading(false)
      },
      (error) => {
        let message = 'Unable to get your location.'
        if (error.code === 1) message = 'Please allow location access to use this feature.'
        showToast(message, 'warning')
        setIsLoading(false)
      }
    )
  }

  // Mobile modal handlers
  const openModal = (type) => {
    let currentValue = ''
    if (type === 'gender') currentValue = gender || 'Any Gender'
    if (type === 'price') currentValue = price || 'Any Price'
    if (type === 'sharing') currentValue = sharing || 'Any Type'
    setTempValue(currentValue)
    setMobileModal(type)
  }

  const closeModal = () => {
    setMobileModal(null)
    setTempValue('')
  }

  const applyModal = () => {
    if (mobileModal === 'gender') {
      setGender(tempValue === 'Any Gender' ? '' : tempValue)
    } else if (mobileModal === 'price') {
      setPrice(tempValue === 'Any Price' ? '' : tempValue)
    } else if (mobileModal === 'sharing') {
      setSharing(tempValue === 'Any Type' ? '' : tempValue)
    }
    closeModal()

    // Close mobile menu when filter is applied
    if (onAction) onAction()
  }

  const clearModal = () => {
    if (mobileModal === 'gender') setTempValue('Any Gender')
    if (mobileModal === 'price') setTempValue('Any Price')
    if (mobileModal === 'sharing') setTempValue('Any Type')
  }

  const clearFilter = (filterType, e) => {
    e.stopPropagation()
    if (filterType === 'gender') setGender('')
    if (filterType === 'price') setPrice('')
    if (filterType === 'sharing') setSharing('')
  }

  const getFilterDisplay = (filterType) => {
    if (filterType === 'gender') return gender || 'Any gender'
    if (filterType === 'price') return price || 'Any price'
    return sharing || 'Any type'
  }

  const getModalTitle = () => {
    if (mobileModal === 'gender') return 'Select Gender'
    if (mobileModal === 'price') return 'Select Price Range'
    if (mobileModal === 'sharing') return 'Select Sharing Type'
    return ''
  }

  const getModalOptions = () => {
    if (mobileModal === 'gender') return GENDER_OPTIONS
    if (mobileModal === 'price') return PRICE_OPTIONS
    return SHARING_OPTIONS
  }

  const togglePanel = (panel) => setActivePanel(prev => prev === panel ? null : panel)

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) {
        setActivePanel(null)
        setShowDropdown(false)
        setShowMobileDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="sb-wrapper" ref={barRef}>
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

      {/* ========== DESKTOP SEARCH BAR (≥768px) ========== */}
      <div className={`sb-bar ${compact ? 'sb-bar-compact' : ''}`}>
        {/* Location */}
        <div className={`sb-segment sb-segment-where ${activePanel === 'location' ? 'active' : ''}`} onClick={() => togglePanel('location')}>
          <span className="sb-label">WHERE</span>
          <span className={`sb-value ${!location ? 'placeholder' : ''}`}>{location || 'Search location'}</span>
          
          {activePanel === 'location' && (
            <div className="sb-dropdown sb-dropdown-location" onClick={e => e.stopPropagation()}>
              <div className="sb-location-input-row">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                </svg>
                <input 
                  autoFocus 
                  type="text" 
                  className="sb-location-input" 
                  placeholder="Search areas, localities..." 
                  value={searchQuery || location} 
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch()
                    if (e.key === 'Escape') setShowDropdown(false)
                  }}
                />
              </div>
              
              {showDropdown && suggestions.length > 0 && (
                <div className="sb-dropdown-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {suggestions.map((item, idx) => (
                    <button 
                      key={item.place_id || idx}
                      className={`sb-suggestion ${idx === activeIndex ? 'active' : ''}`}
                      onClick={() => {
                        const label = item.display_name
                        setSearchQuery(label)
                        setLocation(label)
                        setShowDropdown(false)
                        handleSearch(label)
                      }}
                    >
                      <div className="sb-suggestion-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                        </svg>
                      </div>
                      <div className="sb-suggestion-text">
                        <span className="sb-suggestion-title">{item.display_name}</span>
                        {item.type && (
                          <span className="sb-suggestion-sub">{item.type}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              <button className="sb-suggestion sb-suggestion-nearby" onClick={handleGeolocation}>
                <div className="sb-suggestion-icon sb-suggestion-icon-nearby">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#155DFC" strokeWidth="2">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M12 2V5M12 19V22M2 12H5M19 12H22"/>
                  </svg>
                </div>
                <div className="sb-suggestion-text">
                  <span className="sb-suggestion-title">Use my current location</span>
                  <span className="sb-suggestion-sub">Find PGs around you</span>
                </div>
              </button>
            </div>
          )}
        </div>

        <div className="sb-divider" />

        {/* Gender */}
        <div className={`sb-segment ${activePanel === 'gender' ? 'active' : ''}`} onClick={() => togglePanel('gender')}>
          <span className="sb-label">GENDER</span>
          <span className={`sb-value ${!gender ? 'placeholder' : ''}`}>{gender || 'Any gender'}</span>
          {activePanel === 'gender' && (
            <div className="sb-dropdown sb-dropdown-options">
              {GENDER_OPTIONS.map(opt => (
                <button key={opt} className={`sb-option ${gender === opt || (!gender && opt === 'Any Gender') ? 'selected' : ''}`} 
                  onClick={() => { setGender(opt === 'Any Gender' ? '' : opt); setActivePanel(null); }}>
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="sb-divider" />

        {/* Price */}
        <div className={`sb-segment ${activePanel === 'price' ? 'active' : ''}`} onClick={() => togglePanel('price')}>
          <span className="sb-label">PRICE</span>
          <span className={`sb-value ${!price ? 'placeholder' : ''}`}>{price || 'Any price'}</span>
          {activePanel === 'price' && (
            <div className="sb-dropdown sb-dropdown-options">
              {PRICE_OPTIONS.map(opt => (
                <button key={opt} className={`sb-option ${price === opt || (!price && opt === 'Any Price') ? 'selected' : ''}`}
                  onClick={() => { setPrice(opt === 'Any Price' ? '' : opt); setActivePanel(null); }}>
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="sb-divider" />

        {/* Sharing */}
        <div className={`sb-segment ${activePanel === 'sharing' ? 'active' : ''}`} onClick={() => togglePanel('sharing')}>
          <span className="sb-label">SHARING</span>
          <span className={`sb-value ${!sharing ? 'placeholder' : ''}`}>{sharing || 'Any type'}</span>
          {activePanel === 'sharing' && (
            <div className="sb-dropdown sb-dropdown-options sb-dropdown-right">
              {SHARING_OPTIONS.map(opt => (
                <button key={opt} className={`sb-option ${sharing === opt || (!sharing && opt === 'Any Type') ? 'selected' : ''}`}
                  onClick={() => { setSharing(opt === 'Any Type' ? '' : opt); setActivePanel(null); }}>
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>

        <button className="sb-search-btn" onClick={() => handleSearch()} disabled={isLoading}>
          <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg><span className="sb-search-btn-text">Search</span></>
        </button>
      </div>

      {/* ========== MOBILE CARD ========== */}
      <div className="sb-mobile-card">
        <div className="sb-mobile-location">
          <div className="sb-mobile-location-label">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            <span>WHERE TO STAY?</span>
          </div>
          <div className="sb-mobile-location-input-wrapper">
            <div className="sb-mobile-location-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
            </div>
            <input 
              type="text" 
              className="sb-mobile-location-input"
              placeholder="Search area, locality, or landmark"
              value={mobileSearchQuery || location}
              onChange={handleMobileInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleMobileSearch()
                if (e.key === 'Escape') setShowMobileDropdown(false)
              }}
              onFocus={() => {
                if (mobileSuggestions.length > 0) setShowMobileDropdown(true)
              }}
            />
          </div>
          
          {/* Mobile Suggestions - using new API response format */}
          {showMobileDropdown && mobileSuggestions.length > 0 && (
            <div className="sb-mobile-suggestions">
              {mobileSuggestions.map((item, idx) => (
                <button 
                  key={item.place_id || idx}
                  className={`sb-mobile-suggestion ${idx === mobileActiveIndex ? 'active' : ''}`}
                  onClick={() => {
                    const label = item.display_name
                    setMobileSearchQuery(label)
                    setLocation(label)
                    setShowMobileDropdown(false)
                    handleMobileSearch(label)
                  }}
                >
                  <div className="sb-mobile-suggestion-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                    </svg>
                  </div>
                  <div className="sb-mobile-suggestion-text">
                    <span className="sb-mobile-suggestion-title">{item.display_name}</span>
                    {item.type && (
                      <span className="sb-mobile-suggestion-sub">{item.type}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          
          <div className="sb-mobile-current-location" onClick={handleGeolocation}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#155DFC" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 2V5M12 19V22M2 12H5M19 12H22"/>
            </svg>
            <span>Use my current location</span>
          </div>
        </div>

        <div className="sb-mobile-filters">
          <div className="sb-mobile-filters-scroll">
            <div 
              className={`sb-mobile-filter-chip ${gender ? 'active' : ''}`}
              onClick={() => openModal('gender')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="4"/>
                <path d="M12 2V6M12 18V22M2 12H6M18 12H22"/>
              </svg>
              <span>{getFilterDisplay('gender')}</span>
              {gender && <span className="clear-icon" onClick={(e) => clearFilter('gender', e)}>✕</span>}
            </div>

            <div 
              className={`sb-mobile-filter-chip ${price ? 'active' : ''}`}
              onClick={() => openModal('price')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20M17 7H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
              <span>{getFilterDisplay('price')}</span>
              {price && <span className="clear-icon" onClick={(e) => clearFilter('price', e)}>✕</span>}
            </div>

            <div 
              className={`sb-mobile-filter-chip ${sharing ? 'active' : ''}`}
              onClick={() => openModal('sharing')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
              </svg>
              <span>{getFilterDisplay('sharing')}</span>
              {sharing && <span className="clear-icon" onClick={(e) => clearFilter('sharing', e)}>✕</span>}
            </div>
          </div>
        </div>

        <button className="sb-mobile-search-btn" onClick={() => handleMobileSearch()} disabled={isLoading}>
          {isLoading ? 'Searching...' : (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35"/>
              </svg>
              Search PGs
            </>
          )}
        </button>
      </div>

      {/* Modal */}
      {mobileModal && (
        <div className="sb-mobile-modal-overlay" onClick={closeModal}>
          <div className="sb-mobile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sb-mobile-modal-header">
              <h3>{getModalTitle()}</h3>
              <div className="sb-mobile-modal-close" onClick={closeModal}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6L18 18"/>
                </svg>
              </div>
            </div>
            <div className="sb-mobile-modal-content">
              {getModalOptions().map(opt => (
                <div 
                  key={opt}
                  className={`sb-mobile-modal-option ${tempValue === opt ? 'selected' : ''}`}
                  onClick={() => setTempValue(opt)}
                >
                  <span>{opt}</span>
                  {tempValue === opt && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#155DFC" strokeWidth="2.5">
                      <path d="M20 6L9 17L4 12"/>
                    </svg>
                  )}
                </div>
              ))}
            </div>
            <div className="sb-mobile-modal-footer">
              <button className="sb-mobile-modal-clear" onClick={clearModal}>Clear</button>
              <button className="sb-mobile-modal-apply" onClick={applyModal}>Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SearchBar